import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Rocket, Copy, ExternalLink, Sparkles, ShieldCheck, TrendingUp, Zap, QrCode, X as CloseX, Undo2, Download } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { renderNebulaReceiptPdf } from "./nebulaReceiptPdf";

/**
 * 🛸 Nebula Inbound Settlement Vault
 * ----------------------------------
 * Premium Tax Shield cockpit for issuing Stripe Connect-powered payment
 * links to a merchant's clients. 1% platform application fee is skimmed
 * BEFORE the merchant sees balance (Choice 1 deducted model). Successful
 * payments cascade net-of-fees into the Cash Flash statutory buffer engine.
 *
 * Cosmic canvas:
 *   • drifting nebula clouds  (2 layered radial-gradients, slow keyframe pan)
 *   • pulsing starlight arrays (dot pattern, opacity breathing)
 *   • stardust trails that shift hue when a row is hovered
 */
export function NebulaSettlementVault() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [recurring, setRecurring] = useState(false);       // Billing Type toggle
  const [interval, setBillingInterval] = useState("month"); // month | year
  const [busy, setBusy] = useState(false);
  const [onboardBusy, setOnboardBusy] = useState(false);
  const [hoverInvoice, setHoverInvoice] = useState(null);
  const [qrTarget, setQrTarget] = useState(null);         // invoice obj shown in QR modal
  const [refundBusy, setRefundBusy] = useState(null);     // invoice_id being refunded
  const [pulseTicker, setPulseTicker] = useState(false);  // fires when a paid webhook lands
  const lastPaidCountRef = useRef(0);
  const lastPaidSettledRef = useRef(0);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/nebula/status");
      setStatus(data);
    } catch {
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    // Poll every 8s so an incoming webhook (real or simulated) lights up
    // the earnings ticker within a heartbeat of Stripe firing.
    const iv = setInterval(loadStatus, 8000);
    // Frontend event bus — the simulate-payment handler and any future
    // in-app webhook echo can dispatch this to fire the ticker immediately.
    const onPaymentReceived = () => {
      setPulseTicker(true);
      setTimeout(() => setPulseTicker(false), 3200);
    };
    window.addEventListener("nebula-payment-received", onPaymentReceived);
    return () => {
      clearInterval(iv);
      window.removeEventListener("nebula-payment-received", onPaymentReceived);
    };
  }, [loadStatus]);

  // Detect a paid-count OR settled-total increase between polls and light
  // the ticker automatically — the "money-landing dopamine moment".
  useEffect(() => {
    if (!status) return;
    const count = status.totals?.count_paid || 0;
    const settled = status.totals?.settled_cents || 0;
    if (
      lastPaidCountRef.current > 0 && count > lastPaidCountRef.current
      || lastPaidSettledRef.current > 0 && settled > lastPaidSettledRef.current
    ) {
      setPulseTicker(true);
      setTimeout(() => setPulseTicker(false), 3200);
    }
    lastPaidCountRef.current = count;
    lastPaidSettledRef.current = settled;
  }, [status]);

  const onboarded = !!status?.onboarded;
  const demoMode = status?.mode === "demo";
  const invoices = status?.invoices || [];
  const totals = status?.totals || {};

  const dollars = useMemo(() => {
    const raw = String(amount || "").replace(/[^0-9.]/g, "");
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }, [amount]);
  const feePreviewCents = Math.max(1, Math.round(dollars * 100) * 0.01);
  const stripeFeePreviewCents = dollars > 0 ? Math.round(dollars * 100 * 0.029) + 30 : 0;
  const netPreviewCents = Math.max(0, Math.round(dollars * 100) - feePreviewCents - stripeFeePreviewCents);

  const handleOnboard = async () => {
    if (onboardBusy) return;
    setOnboardBusy(true);
    const toastId = toast.loading("🛰 Booking Stripe Nebula seat…");
    try {
      const returnUrl = `${window.location.origin}/dashboard?nebula=onboarded`;
      const { data } = await api.post("/v1/nebula/onboard", { return_url: returnUrl });
      toast.success(
        data.mode === "demo"
          ? "Demo Nebula onboarded — link generation is live"
          : "Redirecting to Stripe KYC…",
        { id: toastId },
      );
      if (data.mode === "live" && data.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        await loadStatus();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed", { id: toastId });
    } finally {
      setOnboardBusy(false);
    }
  };

  const handleGenerate = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    if (dollars < 0.5) return toast.error("Amount must be at least $0.50");
    if (!description.trim()) return toast.error("Add a short description");
    setBusy(true);
    const toastId = toast.loading("⚡ Warping invoice into Nebula…");
    try {
      const { data } = await api.post("/v1/nebula/generate-link", {
        amount: dollars,
        description: description.trim(),
        recurring,
        interval: recurring ? interval : null,
      });
      try {
        await navigator.clipboard.writeText(data.short_link);
        toast.success("Nebula link generated · copied to clipboard", { id: toastId });
      } catch {
        toast.success("Nebula link generated", { id: toastId });
      }
      setAmount("");
      setDescription("");
      await loadStatus();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Link generation failed", { id: toastId });
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Copied Nebula link", { duration: 1400 });
    } catch { /* ignore */ }
  };

  const handleSimulate = async (invoice_id) => {
    const toastId = toast.loading("🌌 Simulating client payment…");
    try {
      const { data } = await api.post("/v1/nebula/simulate-payment", { invoice_id });
      toast.success(`Paid · $${(data.net_cents / 100).toFixed(2)} cascaded to Cash Flash`, { id: toastId });
      await loadStatus();
      window.dispatchEvent(new CustomEvent("cash-flash-refresh"));
      window.dispatchEvent(new CustomEvent("nebula-payment-received", { detail: { invoice_id, net_cents: data.net_cents } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed", { id: toastId });
    }
  };

  const handleReceiptPdf = async (inv) => {
    const toastId = toast.loading("📄 Rendering branded receipt…");
    try {
      // Expose API base for the PDF logo fetch (async image load).
      if (typeof window !== "undefined") window.__RS_API_BASE = process.env.REACT_APP_BACKEND_URL || "";
      await renderNebulaReceiptPdf({
        invoice: inv,
        merchant: {
          user_id: user?.user_id,
          name: user?.name || user?.email,
          email: user?.email,
          address: user?.business_address,
          merchant_logo_ext: user?.merchant_logo_ext,
          merchant_logo_updated_at: user?.merchant_logo_updated_at,
        },
      });
      toast.success("Receipt PDF downloaded", { id: toastId });
    } catch (e) {
      toast.error(e?.message || "PDF render failed", { id: toastId });
    }
  };

  const handleRefund = async (invoice_id, gross_dollars) => {
    if (refundBusy) return;
    const yes = window.confirm(
      `Refund $${gross_dollars.toFixed(2)} on invoice ${invoice_id.slice(0, 12)}?\n\n` +
      `The 1% platform application fee STAYS in your Receipt Sheets platform balance (refund_application_fee=false). ` +
      `A negative rebalancing row will be written to the Cash Flash ledger for audit trail.`
    );
    if (!yes) return;
    setRefundBusy(invoice_id);
    const toastId = toast.loading("↩️ Firing refund · platform fee retained…");
    try {
      const { data } = await api.post("/v1/nebula/refund", { invoice_id });
      toast.success(
        `Refunded $${(data.refunded_cents / 100).toFixed(2)} · $${(data.platform_fee_retained_cents / 100).toFixed(2)} platform fee retained`,
        { id: toastId, duration: 5000 },
      );
      await loadStatus();
      window.dispatchEvent(new CustomEvent("cash-flash-refresh"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed", { id: toastId });
    } finally {
      setRefundBusy(null);
    }
  };

  return (
    <div
      data-testid="nebula-settlement-vault"
      className="relative rounded-2xl overflow-hidden border p-5"
      style={{
        borderColor: "rgba(139,92,246,0.55)",
        background: "linear-gradient(180deg, rgba(15,7,45,0.92), rgba(30,12,72,0.94))",
        boxShadow: "0 0 32px rgba(139,92,246,0.35), 0 0 0 1px rgba(139,92,246,0.15) inset",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
      }}
    >
      {/* Cosmic canvas layer */}
      <div className="nebula-canvas" aria-hidden />
      <div className="nebula-stars" aria-hidden />
      <div className="nebula-stardust" aria-hidden />

      <div className="relative z-10">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Rocket size={16} className="text-fuchsia-300" />
              <h3 className="text-white text-lg font-bold tracking-tight">
                🛸 Nebula Inbound Settlement Vault
              </h3>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-fuchsia-300 mt-1">
              Stripe Connect · 1% platform skim · cascades to Cash Flash
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Earnings ticker — pulses neon-emerald the instant a paid
                webhook lands (poll delta OR nebula-payment-received event). */}
            <span
              data-testid="nebula-earnings-ticker"
              className={`nebula-ticker inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-md ${pulseTicker ? "nebula-ticker-pulse" : ""}`}
              style={{
                background: pulseTicker ? "rgba(16,185,129,0.22)" : "rgba(15,7,45,0.55)",
                border: `1px solid ${pulseTicker ? "rgba(16,185,129,0.85)" : "rgba(139,92,246,0.35)"}`,
                color: pulseTicker ? "#6ee7b7" : "#c4b5fd",
                transition: "background 260ms ease, border-color 260ms ease, color 260ms ease",
              }}
              title="Pulses neon-emerald the moment a client payment webhook fires"
            >
              <span
                className={pulseTicker ? "nebula-ticker-dot-on" : "nebula-ticker-dot-idle"}
                style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: pulseTicker ? "#10b981" : "#64748b",
                  boxShadow: pulseTicker ? "0 0 12px rgba(16,185,129,0.95)" : "none",
                  display: "inline-block",
                }}
              />
              {pulseTicker ? "💸 Payment Landed" : "Awaiting Client"}
            </span>
            {onboarded ? (
              <span
                data-testid="nebula-status-chip"
                className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md"
                style={{
                  background: demoMode ? "rgba(245,158,11,0.14)" : "rgba(16,185,129,0.14)",
                  border: `1px solid ${demoMode ? "rgba(245,158,11,0.55)" : "rgba(16,185,129,0.55)"}`,
                  color: demoMode ? "#fbbf24" : "#6ee7b7",
                }}
              >
                <ShieldCheck size={10} /> {demoMode ? "Demo · Connect not enabled" : "Live · Charges Enabled"}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded-md border border-rose-500/45 bg-rose-500/10 text-rose-200">
                <Sparkles size={10} /> Onboarding Required
              </span>
            )}
          </div>
        </div>

        {/* Onboarding CTA */}
        {!onboarded ? (
          <div
            data-testid="nebula-onboard-cta"
            className="rounded-xl p-4 mb-4 flex items-center justify-between flex-wrap gap-3"
            style={{
              background: "rgba(139,92,246,0.09)",
              border: "1px dashed rgba(139,92,246,0.55)",
            }}
          >
            <div className="max-w-xl">
              <div className="text-white text-sm font-semibold mb-1">
                🛰 Link your Stripe payout vault to start clearing client payments
              </div>
              <div className="text-slate-300 text-xs leading-relaxed">
                Stripe Express handles KYC, bank verification, and payouts to your business bank on file. Once you're onboarded, every Nebula link auto-skims a 1% platform fee and cascades the net into Cash Flash statutory reserves.
              </div>
            </div>
            <button
              type="button"
              onClick={handleOnboard}
              disabled={onboardBusy}
              data-testid="nebula-onboard-btn"
              className="px-4 h-11 rounded-lg text-white text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:-translate-y-px transition-transform disabled:opacity-70"
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #ec4899)",
                boxShadow: "0 12px 26px -10px rgba(139,92,246,0.7)",
                letterSpacing: "0.14em",
              }}
            >
              <Rocket size={14} /> {onboardBusy ? "Booking seat…" : "🛰 Onboard to Nebula"}
            </button>
          </div>
        ) : (
          <>
            {/* Generation form */}
            <form
              onSubmit={handleGenerate}
              data-testid="nebula-generate-form"
              className="rounded-xl p-4 mb-4"
              style={{
                background: "rgba(15,7,45,0.55)",
                border: "1px solid rgba(139,92,246,0.35)",
              }}
            >
              {/* Billing type toggle · One-Time ↔ Recurring Subscription */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-[9px] font-mono uppercase tracking-widest text-fuchsia-200">
                  Billing Type:
                </span>
                <div
                  role="tablist"
                  aria-label="Billing type"
                  data-testid="nebula-billing-toggle"
                  className="inline-flex rounded-lg overflow-hidden"
                  style={{ border: "1px solid rgba(139,92,246,0.45)" }}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={!recurring}
                    onClick={() => setRecurring(false)}
                    data-testid="nebula-billing-onetime"
                    className={`px-3 h-8 text-[10px] font-mono uppercase tracking-widest transition-colors ${!recurring ? "text-white" : "text-slate-400 hover:text-slate-200"}`}
                    style={{ background: !recurring ? "linear-gradient(135deg,#8b5cf6,#ec4899)" : "transparent" }}
                  >
                    One-Time
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={recurring}
                    onClick={() => setRecurring(true)}
                    data-testid="nebula-billing-recurring"
                    className={`px-3 h-8 text-[10px] font-mono uppercase tracking-widest transition-colors ${recurring ? "text-white" : "text-slate-400 hover:text-slate-200"}`}
                    style={{ background: recurring ? "linear-gradient(135deg,#8b5cf6,#ec4899)" : "transparent" }}
                  >
                    Recurring Subscription
                  </button>
                </div>
                {recurring && (
                  <select
                    value={interval}
                    onChange={(e) => setBillingInterval(e.target.value)}
                    data-testid="nebula-billing-interval"
                    className="h-8 rounded-md px-2 bg-slate-900/70 border border-fuchsia-500/40 text-fuchsia-100 text-[10px] font-mono uppercase tracking-widest focus:outline-none"
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Annual</option>
                  </select>
                )}
                {recurring && (
                  <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-300">
                    · 1% platform fee locked per renewal
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-3 items-end">
                <label className="block">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-fuchsia-200 mb-1">
                    Client Invoice Amount ($)
                  </div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="1,250.00"
                    data-testid="nebula-amount-input"
                    className="w-full h-11 rounded-lg px-3 text-white text-sm font-mono bg-slate-900/70 border border-fuchsia-500/40 focus:outline-none focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/30"
                  />
                </label>
                <label className="block">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-fuchsia-200 mb-1">
                    Service / Product Description
                  </div>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Q3 consulting retainer · Acme Corp"
                    data-testid="nebula-description-input"
                    className="w-full h-11 rounded-lg px-3 text-white text-sm bg-slate-900/70 border border-fuchsia-500/40 focus:outline-none focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/30"
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy || dollars < 0.5 || !description.trim()}
                  data-testid="nebula-generate-btn"
                  className="h-11 px-4 rounded-lg text-white text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:-translate-y-px transition-transform disabled:opacity-60"
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
                    boxShadow: "0 12px 26px -10px rgba(139,92,246,0.7)",
                    letterSpacing: "0.14em",
                  }}
                >
                  <Zap size={14} /> {busy ? "Warping…" : "Generate Nebula Link"}
                </button>
              </div>

              {dollars > 0 && (
                <div
                  data-testid="nebula-fee-preview"
                  className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] font-mono"
                >
                  <div className="rounded-md px-3 py-2 bg-slate-900/60 border border-slate-600/40">
                    <div className="text-slate-400 uppercase tracking-widest">Gross</div>
                    <div className="text-white text-sm tabular-nums">${dollars.toFixed(2)}</div>
                  </div>
                  <div className="rounded-md px-3 py-2 bg-slate-900/60 border border-slate-600/40">
                    <div className="text-rose-300 uppercase tracking-widest">Stripe Fee</div>
                    <div className="text-white text-sm tabular-nums">−${(stripeFeePreviewCents / 100).toFixed(2)}</div>
                  </div>
                  <div className="rounded-md px-3 py-2 bg-slate-900/60 border border-fuchsia-500/50">
                    <div className="text-fuchsia-300 uppercase tracking-widest">1% Platform</div>
                    <div className="text-white text-sm tabular-nums">−${(feePreviewCents / 100).toFixed(2)}</div>
                  </div>
                  <div className="rounded-md px-3 py-2 bg-emerald-500/10 border border-emerald-500/45">
                    <div className="text-emerald-300 uppercase tracking-widest">Merchant Net</div>
                    <div className="text-emerald-200 text-sm font-bold tabular-nums">${(netPreviewCents / 100).toFixed(2)}</div>
                  </div>
                </div>
              )}
            </form>

            {/* Totals odometers */}
            <div className="grid grid-cols-3 gap-2 mb-3 text-[10px] font-mono">
              <div className="rounded-md px-3 py-2 bg-slate-900/60 border border-emerald-500/40">
                <div className="text-emerald-300 uppercase tracking-widest">Settled Net (YTD)</div>
                <div className="text-white text-lg tabular-nums font-bold" data-testid="nebula-total-settled">
                  ${((totals.settled_cents || 0) / 100).toFixed(2)}
                </div>
              </div>
              <div className="rounded-md px-3 py-2 bg-slate-900/60 border border-fuchsia-500/40">
                <div className="text-fuchsia-300 uppercase tracking-widest">Platform Skim</div>
                <div className="text-white text-lg tabular-nums font-bold" data-testid="nebula-total-fee">
                  ${((totals.platform_fee_cents || 0) / 100).toFixed(2)}
                </div>
              </div>
              <div className="rounded-md px-3 py-2 bg-slate-900/60 border border-cyan-500/40">
                <div className="text-cyan-300 uppercase tracking-widest">Open · Paid</div>
                <div className="text-white text-lg tabular-nums font-bold" data-testid="nebula-total-counts">
                  {totals.count_open || 0} · {totals.count_paid || 0}
                </div>
              </div>
            </div>

            {/* Invoice ledger */}
            {invoices.length === 0 ? (
              <div
                data-testid="nebula-invoices-empty"
                className="rounded-md px-3 py-6 text-center text-[10px] font-mono uppercase tracking-widest text-slate-400 border border-dashed border-fuchsia-500/25"
              >
                No Nebula links generated yet · your first invoice will appear here as a glowing row
              </div>
            ) : (
              <ul data-testid="nebula-invoices-list" className="space-y-1.5">
                {invoices.map((inv) => {
                  const paid = inv.status === "paid";
                  return (
                    <li
                      key={inv.invoice_id}
                      data-testid={`nebula-invoice-${inv.invoice_id}`}
                      onMouseEnter={() => setHoverInvoice(inv.invoice_id)}
                      onMouseLeave={() => setHoverInvoice(null)}
                      className={`nebula-row rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap ${hoverInvoice === inv.invoice_id ? "nebula-row-hover" : ""}`}
                      style={{
                        background: paid ? "rgba(16,185,129,0.09)" : "rgba(15,7,45,0.55)",
                        border: `1px solid ${paid ? "rgba(16,185,129,0.45)" : "rgba(139,92,246,0.3)"}`,
                      }}
                    >
                      <div className="flex-1 min-w-[160px]">
                        <div className="text-white text-xs font-semibold truncate">{inv.description}</div>
                        <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400">
                          {inv.invoice_id.slice(0, 12)} · {new Date(inv.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-white text-sm font-bold tabular-nums">
                          ${((inv.amount_cents || 0) / 100).toFixed(2)}
                        </div>
                        <div className={`text-[9px] font-mono uppercase tracking-widest ${paid ? "text-emerald-300" : "text-fuchsia-300"}`}>
                          {paid ? `+$${((inv.net_cents || 0) / 100).toFixed(2)} net` : "awaiting"}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleCopy(inv.short_link)}
                          data-testid={`nebula-copy-${inv.invoice_id}`}
                          className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-white/5"
                          title="Copy Nebula link"
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setQrTarget(inv)}
                          data-testid={`nebula-qr-${inv.invoice_id}`}
                          className="p-2 rounded-md text-slate-300 hover:text-fuchsia-200 hover:bg-fuchsia-500/10"
                          title="Show scan-to-pay QR code"
                        >
                          <QrCode size={12} />
                        </button>
                        <a
                          href={inv.short_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`nebula-open-${inv.invoice_id}`}
                          className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-white/5"
                          title="Open"
                        >
                          <ExternalLink size={12} />
                        </a>
                        {demoMode && !paid && (
                          <button
                            type="button"
                            onClick={() => handleSimulate(inv.invoice_id)}
                            data-testid={`nebula-simulate-${inv.invoice_id}`}
                            className="px-2 h-8 rounded-md text-[9px] font-mono uppercase tracking-widest text-emerald-200 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/25"
                            title="Simulate client payment (demo mode)"
                          >
                            <TrendingUp size={10} className="inline mr-1" /> Simulate Paid
                          </button>
                        )}
                        {paid && inv.status !== "refunded" && (
                          <button
                            type="button"
                            onClick={() => handleReceiptPdf(inv)}
                            data-testid={`nebula-receipt-${inv.invoice_id}`}
                            className="p-2 rounded-md text-emerald-200 hover:text-white hover:bg-emerald-500/15"
                            title="Download branded receipt PDF"
                          >
                            <Download size={12} />
                          </button>
                        )}
                        {paid && inv.status !== "refunded" && (
                          <button
                            type="button"
                            onClick={() => handleRefund(inv.invoice_id, (inv.amount_cents || 0) / 100)}
                            disabled={refundBusy === inv.invoice_id}
                            data-testid={`nebula-refund-${inv.invoice_id}`}
                            className="px-2 h-8 rounded-md text-[9px] font-mono uppercase tracking-widest text-rose-200 border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/25 disabled:opacity-50"
                            title="Refund invoice · 1% platform fee retained"
                          >
                            <Undo2 size={10} className="inline mr-1" />
                            {refundBusy === inv.invoice_id ? "Refunding…" : "Refund"}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      <style>{`
        .nebula-canvas {
          position: absolute; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(ellipse 60% 40% at 20% 30%, rgba(139,92,246,0.35), transparent 60%),
            radial-gradient(ellipse 50% 35% at 80% 70%, rgba(236,72,153,0.28), transparent 60%),
            radial-gradient(ellipse 45% 30% at 50% 100%, rgba(6,182,212,0.22), transparent 60%);
          animation: nebula-drift 24s ease-in-out infinite alternate;
          filter: blur(6px);
        }
        .nebula-stars {
          position: absolute; inset: 0; z-index: 0; pointer-events: none;
          background-image:
            radial-gradient(1px 1px at 12% 18%, rgba(255,255,255,0.85) 50%, transparent 51%),
            radial-gradient(1px 1px at 34% 62%, rgba(255,255,255,0.7)  50%, transparent 51%),
            radial-gradient(1.5px 1.5px at 55% 22%, rgba(255,255,255,0.9) 50%, transparent 51%),
            radial-gradient(1px 1px at 77% 74%, rgba(255,255,255,0.75) 50%, transparent 51%),
            radial-gradient(1px 1px at 88% 34%, rgba(255,255,255,0.6)  50%, transparent 51%),
            radial-gradient(1px 1px at 44% 88%, rgba(255,255,255,0.8)  50%, transparent 51%);
          animation: nebula-twinkle 3.4s ease-in-out infinite;
        }
        .nebula-stardust {
          position: absolute; inset: -10% -5%; z-index: 0; pointer-events: none;
          background: linear-gradient(115deg, transparent 0%, rgba(139,92,246,0.08) 40%, transparent 60%, rgba(236,72,153,0.08) 80%, transparent 100%);
          animation: nebula-stardust 18s linear infinite;
          filter: blur(12px);
        }
        .nebula-row { transition: background 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease, transform 0.25s ease; }
        .nebula-row-hover {
          transform: translateX(2px);
          box-shadow: 0 0 22px rgba(139,92,246,0.35);
          background: linear-gradient(90deg, rgba(139,92,246,0.14), rgba(236,72,153,0.14)) !important;
          border-color: rgba(236,72,153,0.6) !important;
        }
        @keyframes nebula-drift {
          0%   { transform: translate(0,0) scale(1); }
          50%  { transform: translate(-6px,4px) scale(1.03); }
          100% { transform: translate(4px,-3px) scale(1); }
        }
        @keyframes nebula-twinkle {
          0%,100% { opacity: 0.85; }
          50%     { opacity: 0.55; }
        }
        @keyframes nebula-stardust {
          0%   { transform: translateX(-6%); }
          100% { transform: translateX(6%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nebula-canvas, .nebula-stars, .nebula-stardust { animation: none !important; }
          .nebula-ticker-pulse { animation: none !important; }
        }
        /* Earnings ticker — neon-emerald breathing glow on webhook fire. */
        @keyframes nebula-ticker-pulse-anim {
          0%, 100% { box-shadow: 0 0 0 1px rgba(16,185,129,0.35), 0 0 14px rgba(16,185,129,0.55); transform: scale(1); }
          50%      { box-shadow: 0 0 0 2px rgba(16,185,129,0.85), 0 0 30px rgba(16,185,129,0.95); transform: scale(1.04); }
        }
        .nebula-ticker-pulse { animation: nebula-ticker-pulse-anim 900ms ease-in-out infinite; }
        @keyframes nebula-dot-ping {
          0%   { transform: scale(1);   opacity: 1; }
          80%  { transform: scale(2.2); opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
      `}</style>
      {qrTarget && typeof document !== "undefined" && createPortal(
        <div
          data-testid="nebula-qr-overlay"
          onClick={(ev) => { if (ev.target === ev.currentTarget) setQrTarget(null); }}
          className="fixed inset-0 z-[9800] flex items-center justify-center px-4"
          style={{
            background: "rgba(2,6,23,0.75)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            animation: "nebula-qr-fade 220ms ease-out",
          }}
        >
          <div
            data-testid="nebula-qr-panel"
            className="relative rounded-2xl p-6 overflow-hidden"
            style={{
              background: "linear-gradient(180deg, rgba(30,15,60,0.95), rgba(15,7,45,0.98))",
              border: "1px solid rgba(139,92,246,0.55)",
              boxShadow: "0 30px 70px -20px rgba(139,92,246,0.55), 0 0 0 1px rgba(139,92,246,0.15) inset",
              minWidth: 320,
            }}
          >
            <button
              type="button"
              onClick={() => setQrTarget(null)}
              data-testid="nebula-qr-close"
              className="absolute top-3 right-3 text-slate-400 hover:text-white p-1 rounded"
              aria-label="Close"
            >
              <CloseX size={16} />
            </button>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-fuchsia-300 mb-2">
              🛸 Nebula Scan-to-Pay
            </div>
            <div className="text-white text-sm font-semibold truncate max-w-[260px] mb-1" title={qrTarget.description}>
              {qrTarget.description}
            </div>
            <div className="text-fuchsia-200 text-lg font-bold tabular-nums mb-4">
              ${((qrTarget.amount_cents || 0) / 100).toFixed(2)}
            </div>
            <div
              className="rounded-xl p-4 flex items-center justify-center mx-auto"
              style={{ background: "#ffffff" }}
              data-testid="nebula-qr-canvas"
            >
              <QRCodeSVG
                value={qrTarget.short_link}
                size={224}
                level="M"
                bgColor="#ffffff"
                fgColor="#0f0724"
                marginSize={2}
              />
            </div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 text-center mt-4 max-w-[240px] mx-auto break-all">
              {qrTarget.short_link}
            </div>
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                type="button"
                onClick={() => handleCopy(qrTarget.short_link)}
                data-testid="nebula-qr-copy"
                className="px-3 h-9 rounded-md text-[10px] font-mono uppercase tracking-widest text-fuchsia-200 border border-fuchsia-500/40 bg-fuchsia-500/10 hover:bg-fuchsia-500/25 inline-flex items-center gap-1.5"
              >
                <Copy size={11} /> Copy Link
              </button>
              <a
                href={qrTarget.short_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nebula-qr-open"
                className="px-3 h-9 rounded-md text-[10px] font-mono uppercase tracking-widest text-slate-200 border border-slate-500/40 bg-slate-500/10 hover:bg-slate-500/25 inline-flex items-center gap-1.5"
              >
                <ExternalLink size={11} /> Open
              </a>
            </div>
            <style>{`
              @keyframes nebula-qr-fade { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default NebulaSettlementVault;
